import React from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useOnboarding } from "../context/OnboardingContext";
import { useEffectivePermissions, toSet, can } from "../hooks/useEffectivePermissions";

const CORE_STEPS = [
  {
    id: "step-company-data",
    label: "Cadastrar dados básicos da empresa",
    description: "Complete as informações da empresa para personalizar o workspace.",
    route: "/settings/companies",
  },
  {
    id: "step-connect-channels",
    label: "Conectar canais principais (WhatsApp, e-mail, etc.)",
    description: "Defina por onde seus clientes vão falar com você.",
    route: "/settings/channels",
  },
  {
    id: "step-create-users",
    label: "Cadastrar usuários/atendentes",
    description: "Adicione as pessoas que vão atender os clientes.",
    route: "/settings/users",
  },
  {
    id: "step-first-conversation",
    label: "Atender a primeira solicitação",
    description: "Abra o Chat e faça o primeiro atendimento na plataforma.",
    route: "/chat",
  },
];

function getCompletedCount(completedSteps, visibleSteps) {
  const set = new Set(completedSteps || []);
  return visibleSteps.filter((s) => set.has(s.id)).length;
}

const OnboardingChecklist = ({ open, onClose }) => {
  const { state, markStepCompleted } = useOnboarding();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: permData } = useEffectivePermissions();
  const scopesSet = toSet(permData?.scopes);

  // Filtra os passos conforme as permissões do usuário
  const visibleSteps = CORE_STEPS.filter((step) => {
    switch (step.id) {
      case "step-company-data":
        return can(scopesSet, "companies", "view");
      case "step-connect-channels":
        return can(scopesSet, "channels", "view");
      case "step-create-users":
        return can(scopesSet, "users", "view");
      case "step-first-conversation":
        // qualquer usuário com acesso ao chat se beneficia deste passo
        return true;
      default:
        return true;
    }
  });

  const completedCount = getCompletedCount(state.completedSteps, visibleSteps);
  const total = visibleSteps.length || 0;
  const progress = total > 0 ? (completedCount / total) * 100 : 0;

  const handleGoToStep = (step) => {
    navigate(step.route);
  };

  const handleMarkAllOnly = () => {
    CORE_STEPS.forEach((s) => markStepCompleted(s.id));
    markStepCompleted("core-onboarding-done");
  };

  const handleMarkAllDone = () => {
    handleMarkAllOnly();
    onClose?.();
  };

  const showCompletedBanner = total > 0 && completedCount === total;

  const isOnboardingRoute = location.pathname === "/setup";

  // Se não houver nenhum passo relevante para este usuário, não mostra o checklist
  if (visibleSteps.length === 0) return null;

  if (!open && !isOnboardingRoute) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Comece em poucos minutos</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Sugerimos este passo a passo para deixar seu workspace pronto. Você pode
          concluir agora ou ir marcando conforme for avançando.
        </Typography>

        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Progresso
          </Typography>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ mt: 0.5, height: 8, borderRadius: 4 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
            {completedCount} de {total} passos concluídos
          </Typography>
        </Box>

        {showCompletedBanner && (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: 2,
              border: (theme) => `1px solid ${theme.palette.success.light}`,
              backgroundColor: (theme) => theme.palette.success.light + "22",
            }}
          >
            <Typography variant="body2" color="success.main" fontWeight={600}>
              Parabéns! Seu workspace já está parametrizado para começar a atender.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Você pode revisar estes passos sempre que quiser pelo menu de ajuda.
            </Typography>
          </Box>
        )}

        <List dense>
          {visibleSteps.map((step) => {
            const isCompleted = state.completedSteps?.includes(step.id);
            return (
              <ListItem
                key={step.id}
                sx={{
                  borderRadius: 1.5,
                  mb: 1,
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                }}
                secondaryAction={
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleGoToStep(step)}
                  >
                    Ir para a tela
                  </Button>
                }
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isCompleted}
                      onChange={() => markStepCompleted(step.id)}
                    />
                  }
                  label={
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          sx={{
                            textDecoration: isCompleted ? "line-through" : "none",
                          }}
                        >
                          {step.label}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {step.description}
                        </Typography>
                      }
                    />
                  }
                />
              </ListItem>
            );
          })}
        </List>

        <Box sx={{ mt: 1.5 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={state.completedSteps?.includes("core-onboarding-done") || false}
                onChange={handleMarkAllOnly}
              />
            }
            label={
              <Typography variant="body2">
                Já configurei tudo, marcar onboarding como concluído.
              </Typography>
            }
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => navigate("/setup")}>
          Voltar para o setup guiado
        </Button>
        <Button onClick={onClose}>Continuar explorando</Button>
        <Button variant="contained" onClick={handleMarkAllDone}>
          Marcar como concluído
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OnboardingChecklist;

